// components/ToastContainer.jsx
// Renders stacked toasts. Mount once at app root — no props needed.

import { useState, useEffect, useRef, useCallback } from 'react';
import { _subscribe } from '../utils/toast.js';

const ICONS = {
  success: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  error: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  info: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  ),
  referral: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
};

const COLORS = {
  success:  { bg: 'rgba(46,204,138,.10)',  border: 'rgba(46,204,138,.28)',  icon: '#2ecc8a', text: '#E8E4DC' },
  error:    { bg: 'rgba(255,100,80,.10)',  border: 'rgba(255,100,80,.3)',   icon: '#FF6B55', text: '#E8E4DC' },
  info:     { bg: 'rgba(255,255,255,.06)', border: 'rgba(255,255,255,.12)', icon: 'rgba(232,228,220,.55)', text: '#E8E4DC' },
  referral: { bg: 'rgba(212,168,67,.10)',  border: 'rgba(212,168,67,.28)',  icon: '#D4A843', text: '#E8E4DC' },
};

function Toast({ toast: t, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    setLeaving(true);
    setTimeout(() => onDismiss(t.id), 260);
  }, [t.id, onDismiss]);

  useEffect(() => {
    // Enter
    requestAnimationFrame(() => setVisible(true));
    // Auto-dismiss
    timerRef.current = setTimeout(dismiss, t.duration);
    return () => clearTimeout(timerRef.current);
  }, [dismiss, t.duration]);

  const c = COLORS[t.type] || COLORS.info;

  return (
    <div
      onClick={dismiss}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: '#111318',
        border: `1px solid ${c.border}`,
        borderRadius: 10,
        padding: '11px 13px',
        minWidth: 260, maxWidth: 340,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        cursor: 'pointer',
        position: 'relative', overflow: 'hidden',
        transition: 'opacity .28s ease, transform .28s cubic-bezier(.4,0,.2,1)',
        opacity: visible && !leaving ? 1 : 0,
        transform: visible && !leaving ? 'translateX(0)' : 'translateX(24px)',
        userSelect: 'none',
      }}
    >
      {/* Left accent bar */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        width: 3, background: c.icon, borderRadius: '10px 0 0 10px',
        opacity: .85,
      }} />

      {/* Icon */}
      <span style={{
        width: 26, height: 26, borderRadius: 7, flexShrink: 0,
        background: c.bg, color: c.icon,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginLeft: 4,
      }}>
        {ICONS[t.type] || ICONS.info}
      </span>

      {/* Message */}
      <div style={{
        flex: 1, fontSize: 13, lineHeight: 1.45,
        color: c.text, fontFamily: "'Inter',sans-serif",
        fontWeight: 500, paddingRight: 4,
        alignSelf: 'center',
      }}
        dangerouslySetInnerHTML={{ __html: t.message }}
      />

      {/* Close × */}
      <span style={{
        color: 'rgba(232,228,220,0.25)', fontSize: 15, lineHeight: 1,
        flexShrink: 0, alignSelf: 'center',
        transition: 'color .14s',
      }}>×</span>

      {/* Progress bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0,
        height: 2, background: c.icon, opacity: .45,
        borderRadius: '0 0 10px 10px',
        animation: `toastProgress ${t.duration}ms linear forwards`,
      }} />
    </div>
  );
}

export default function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    return _subscribe(t => {
      setToasts(prev => [...prev, t]);
    });
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  if (toasts.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes toastProgress {
          from { width: 100%; }
          to   { width: 0%;   }
        }
      `}</style>
      <div style={{
        position: 'fixed', bottom: 88, right: 20, zIndex: 99999,
        display: 'flex', flexDirection: 'column-reverse', gap: 8,
        pointerEvents: 'none',
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{ pointerEvents: 'auto' }}>
            <Toast toast={t} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </>
  );
}